import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Chart from 'chart.js/auto';
import { fetchCostSummary, resetCostSummary } from '../../redux/actions/CostSummalryActions';

const CostSummaryAnalytics = () => {
  const dispatch = useDispatch();
  const { loading, error, costSummary = [] } = useSelector((state) => state.costSummary || { costSummary: [] });
  const [startDate, setStartDate] = useState(moment('2025-06-01').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [productType, setProductType] = useState('');
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    dispatch(fetchCostSummary(startDate, endDate, productType));
    return () => dispatch(resetCostSummary());
  }, [dispatch, startDate, endDate, productType]);

  useEffect(() => {
    if (costSummary.length && chartRef.current) {
      const ctx = chartRef.current.getContext('2d');
      const allCategories = [...new Set(costSummary.flatMap(item => Object.keys(item.categoryCosts || {})))];
      
      if (ctx && !chartInstance) {
        const datasets = [
          {
            label: 'Total Cost ($)',
            data: costSummary.map(item => item.totalCost || 0),
            backgroundColor: '#3b82f6',
            borderColor: '#2563eb',
            borderWidth: 2,
            borderRadius: 6,
          },
          ...allCategories.map((category, index) => ({
            label: `${category} Cost ($)`,
            data: costSummary.map(item => item.categoryCosts[category] || 0),
            backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
            borderColor: `hsl(${index * 60}, 70%, 40%)`,
            borderWidth: 2,
            borderRadius: 6,
          })),
        ];
        
        const newChartInstance = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: costSummary.map(item => item._id),
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
                labels: {
                  usePointStyle: true,
                  padding: 20,
                  font: { size: 12, weight: '500' },
                  color: '#6b7280',
                },
              },
              tooltip: {
                backgroundColor: '#1f2937',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#3b82f6',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: '#374151' },
                ticks: {
                  color: '#9ca3af',
                  callback: function(value) {
                    return '$' + value.toLocaleString();
                  },
                },
              },
              x: {
                grid: { display: false },
                ticks: { color: '#9ca3af', maxRotation: 45, minRotation: 0 },
              },
            },
            animation: { duration: 1000, easing: 'easeInOutQuart' },
          },
        });
        setChartInstance(newChartInstance);
      } else if (chartInstance) {
        const allCategories = [...new Set(costSummary.flatMap(item => Object.keys(item.categoryCosts || {})))];
        chartInstance.data.labels = costSummary.map(item => item._id);
        chartInstance.data.datasets = [
          {
            label: 'Total Cost ($)',
            data: costSummary.map(item => item.totalCost || 0),
            backgroundColor: '#3b82f6',
            borderColor: '#2563eb',
            borderWidth: 2,
            borderRadius: 6,
          },
          ...allCategories.map((category, index) => ({
            label: `${category} Cost ($)`,
            data: costSummary.map(item => item.categoryCosts[category] || 0),
            backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
            borderColor: `hsl(${index * 60}, 70%, 40%)`,
            borderWidth: 2,
            borderRadius: 6,
          })),
        ];
        chartInstance.update();
      }
    } else if (chartInstance && !costSummary.length) {
      chartInstance.destroy();
      setChartInstance(null);
    }
    return () => chartInstance?.destroy();
  }, [costSummary, chartInstance, chartRef]);

  // Calculate summary statistics
  const totalCost = costSummary.reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const totalOrders = costSummary.reduce((sum, item) => sum + (item.totalOrders || 0), 0);
  const avgOrderValue = totalOrders > 0 ? totalCost / totalOrders : 0;
  const uniqueProducts = costSummary.length;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-body">Loading cost analytics...</h5>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p className="mb-0">Failed to load cost data: {error}</p>
        </div>
      </div>
    );
  }

  const allCategories = [...new Set(costSummary.flatMap(item => Object.keys(item.categoryCosts || {})))];

  return (
    <div className="container-fluid p-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div className="mb-3 mb-md-0">
              <h1 className="h3 mb-2 fw-bold">💰 Cost Summary Analytics</h1>
              <p className="text-muted mb-0">
                Manufacturing cost analysis for blinds production
                <br />
                <small className="text-warning">
                  Note: Cost data is available only for orders since the costing system update (approx. 2 days ago).
                </small>
              </p>
            </div>
            <div>
              <span className="badge bg-primary fs-6 px-3 py-2 rounded-pill">
                {moment(startDate).format('MMM DD')} - {moment(endDate).format('MMM DD, YYYY')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4 g-3">
        <div className="col-xl-3 col-md-6">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-3 d-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <span className="fs-4">💵</span>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1 fw-medium">Total Cost</h6>
                  <h3 className="mb-0 fw-bold">${totalCost.toLocaleString()}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <div className="bg-success bg-opacity-10 rounded-circle p-3 d-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <span className="fs-4">📦</span>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1 fw-medium">Total Orders</h6>
                  <h3 className="mb-0 fw-bold">{totalOrders.toLocaleString()}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <div className="bg-warning bg-opacity-10 rounded-circle p-3 d-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <span className="fs-4">📊</span>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1 fw-medium">Avg Order Value</h6>
                  <h3 className="mb-0 fw-bold">${avgOrderValue.toFixed(2)}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <div className="bg-info bg-opacity-10 rounded-circle p-3 d-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <span className="fs-4">🏷️</span>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1 fw-medium">Product Types</h6>
                  <h3 className="mb-0 fw-bold">{uniqueProducts}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0 py-3">
              <h5 className="mb-0 fw-semibold">🔍 Filters</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-lg-4 col-md-6">
                  <label htmlFor="startDate" className="form-label fw-medium">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-lg-4 col-md-6">
                  <label htmlFor="endDate" className="form-label fw-medium">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="col-lg-4">
                  <label htmlFor="productType" className="form-label fw-medium">Product Type</label>
                  <input
                    type="text"
                    id="productType"
                    className="form-control"
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    placeholder="Filter by product type..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0 py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-semibold">📈 Cost Breakdown</h5>
                <button className="btn btn-outline-primary btn-sm">
                  📊 Export Chart
                </button>
              </div>
            </div>
            <div className="card-body">
              <div style={{ position: 'relative', height: '400px' }}>
                <canvas ref={chartRef} id="costChart"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0 py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-semibold">📋 Detailed Analysis</h5>
                <button className="btn btn-outline-success btn-sm">
                  📥 Export Data
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-semibold border-0 py-3 ps-4">Product Type</th>
                      <th className="fw-semibold border-0 py-3 text-center">Orders</th>
                      <th className="fw-semibold border-0 py-3 text-end">Total Cost</th>
                      {allCategories.map((category, index) => (
                        <th key={index} className="fw-semibold border-0 py-3 text-end">{category}</th>
                      ))}
                      <th className="fw-semibold border-0 py-3 text-end pe-4">Avg/Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costSummary.map((item) => {
                      const categoryCosts = item.categoryCosts || {};
                      return (
                        <tr key={item._id}>
                          <td className="py-3 ps-4">
                            <div className="fw-medium">{item._id}</div>
                          </td>
                          <td className="py-3 text-center">
                            <span className="badge bg-primary rounded-pill px-3 py-1">
                              {item.totalOrders?.toLocaleString() || '0'}
                            </span>
                          </td>
                          <td className="py-3 text-end fw-bold">
                            ${(item.totalCost || 0).toLocaleString()}
                          </td>
                          {allCategories.map((category, index) => (
                            <td key={index} className="py-3 text-end">
                              ${(categoryCosts[category] || 0).toLocaleString()}
                            </td>
                          ))}
                          <td className="py-3 text-end pe-4">
                            <span className="badge bg-success rounded-pill px-3 py-1">
                              ${(item.avgOrderCost || 0).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(!costSummary || costSummary.length === 0) && (
                  <div className="text-center py-5">
                    <div className="text-muted">
                      <div className="fs-1 mb-3">📊</div>
                      <h6>No data available</h6>
                      <p className="mb-0">Adjust your filters to view cost data. Note: Cost data is only available for orders since the costing system update (approx. 2 days ago).</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostSummaryAnalytics;